/**
 * The Tools box (Synth VR, SRVR-0351): the desktop editor's Rail Smoothing, Note Rotation and Scaling,
 * one button each, the open tool's controls inline underneath. What each tool does is in the store
 * (`rotateArea`, `scaleArea`, `previewRailSmoothing`, … — SRVR-0352 – SRVR-0354) and
 * `store/railSmoothing.ts` (SRVR-0355); this file is only the controls.
 *
 * A tool previews by writing through the store, so closing it, switching tool or leaving the box puts an
 * unapplied preview back rather than keeping it half-done.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useEditorStore, type RailSmoothingParams } from '../../store/editorStore';
import { SMOOTHING_ALGORITHMS, algorithmInfo, paramValue, type SmoothingAlgorithm } from '../../store/railSmoothing';
import GradientSlider from '../ui/GradientSlider';
import { Tooltip, tip } from '../ui/Tooltip';
import { IcRailSmooth, IcRotate, IcScale } from '../ui/icons';
import {
  toolGridStyle, toolCellStyle, toolBtnStyle, toolLabelStyle, panelStyle, hintStyle, statusStyle, rowStyle, pillBtnStyle,
  pillBtnDisabledStyle, primaryPillStyle, selectStyle, checkRowStyle, checkboxStyle, subLabelStyle,
} from './ToolsBox.styles';

export type ToolId = 'smooth' | 'rotate' | 'scale';

const TOOLS: { id: ToolId; label: string; Icon: React.FC<{ size?: number }>; body: string }[] = [
  { id: 'smooth', label: 'Rail Smoothing', Icon: IcRailSmooth, body: 'Smooth the rail that starts on the current beat with one of eleven algorithms, previewing as you drag.' },
  { id: 'rotate', label: 'Note Rotation', Icon: IcRotate, body: 'Turn the notes and rails in the selected area, or on the current beat, about the centre of the grid.' },
  { id: 'scale', label: 'Scaling', Icon: IcScale, body: 'Scale the notes, rails and walls in the selected area, or on the current beat, toward or away from the centre of the grid.' },
];

export default function ToolsBox() {
  const [open, setOpen] = useState<ToolId | null>(null);

  // Leaving the box (another view, another map screen) puts an open preview back.
  useEffect(() => () => useEditorStore.getState().cancelToolPreview(), []);

  const toggle = (id: ToolId) => {
    useEditorStore.getState().cancelToolPreview();
    setOpen(cur => (cur === id ? null : id));
  };

  return (
    <div data-testid="tools-box">
      <div style={toolGridStyle}>
        {TOOLS.map(t => (
          <Tooltip key={t.id} content={tip({ title: t.label, body: t.body })} style={toolCellStyle}>
            <button type="button" onClick={() => toggle(t.id)} style={toolBtnStyle(open === t.id)} aria-pressed={open === t.id}>
              <span className="ic-themed" style={{ display: 'flex' }}><t.Icon size={14} /></span>
              <span style={toolLabelStyle}>{t.label}</span>
            </button>
          </Tooltip>
        ))}
      </div>
      {open === 'smooth' && <RailSmoothingPanel onClose={() => setOpen(null)} />}
      {open === 'rotate' && <RotationPanel />}
      {open === 'scale' && <ScalingPanel />}
    </div>
  );
}

// ── Note Rotation (NoteRotationFunctions.cs) ────────────────────────────────

const ROTATION_RANGE = 360;

function RotationPanel() {
  // Anticlockwise degrees, as the desktop's slider; its readout shows them negated, so clockwise reads positive.
  const [deg, setDeg] = useState(0);
  const degRef = useRef(0);
  const st = () => useEditorStore.getState();
  const step = (d: number) => st().rotateArea(d);

  return (
    <div style={panelStyle} data-testid="tool-rotate">
      <div style={rowStyle}>
        <button type="button" style={pillBtnStyle} onClick={() => step(5)} title="Rotate 5° anticlockwise">↺ 5°</button>
        <button type="button" style={pillBtnStyle} onClick={() => step(1)} title="Rotate 1° anticlockwise">↺ 1°</button>
        <button type="button" style={pillBtnStyle} onClick={() => step(-1)} title="Rotate 1° clockwise">1° ↻</button>
        <button type="button" style={pillBtnStyle} onClick={() => step(-5)} title="Rotate 5° clockwise">5° ↻</button>
      </div>
      <GradientSlider
        inline label="Rotate" labelWidth={40}
        value={(deg + ROTATION_RANGE) / (2 * ROTATION_RANGE)}
        valueText={`${(-deg).toFixed(0)}°`}
        onChange={v => {
          const d = Math.round((v * 2 - 1) * ROTATION_RANGE);
          degRef.current = d;
          setDeg(d);
          if (d) st().previewRotateArea(d);
          else st().cancelToolPreview();
        }}
        onRelease={() => {
          // Release keeps the rotation and the slider springs back to 0, ready for the next turn.
          if (degRef.current) st().commitToolPreview();
          degRef.current = 0;
          setDeg(0);
        }}
      />
      <div style={hintStyle}>
        Turns the notes and rails in the selected area, or on the current beat, about the centre of the
        grid. Drag the slider to preview; release to keep it. Walls are not rotated.
      </div>
    </div>
  );
}

// ── Scaling (ScalerPanelFunctions.cs) ───────────────────────────────────────

type ScaleValue = { x: number; y: number; lock: boolean };
const clampPct = (v: number) => Math.max(0, Math.min(200, Math.round(v)));

const SCALE_RESET: ScaleValue = { x: 100, y: 100, lock: true };

function ScalingPanel() {
  const [value, setValue] = useState<ScaleValue>(SCALE_RESET);
  // The slider reads its handler once per drag, so the handlers read the latest values from here.
  const valueRef = useRef(value);
  valueRef.current = value;
  const st = () => useEditorStore.getState();

  // Every change previews from where the range was when the preview opened; Apply keeps it.
  const onChange = (next: ScaleValue) => {
    setValue(next);
    valueRef.current = next;
    st().previewScaleArea(next.x, next.y);
  };
  // After Apply or Cancel the controls start again from 100 %, so the next change scales what is there now.
  const restart = () => { const next = { ...SCALE_RESET, lock: valueRef.current.lock }; setValue(next); valueRef.current = next; };
  const apply = () => {
    const { x, y } = valueRef.current;
    // A playhead move puts a preview back; Apply then scales what is on the new beat.
    if (st().toolPreview === 'scale') st().commitToolPreview();
    else st().scaleArea(x, y);
    restart();
  };
  const setAxis = (axis: 'x' | 'y', pct: number) => {
    const p = clampPct(pct);
    const cur = valueRef.current;
    // Lock keeps the two equal whichever one moved.
    onChange(cur.lock ? { ...cur, x: p, y: p } : { ...cur, [axis]: p });
  };
  const axisRow = (axis: 'x' | 'y') => (
    <div key={axis} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <GradientSlider
        inline label={axis.toUpperCase()} labelWidth={18}
        value={value[axis] / 200}
        valueText={`${value[axis]}%`}
        onChange={v => setAxis(axis, v * 200)}
        onCommitText={raw => { const n = parseFloat(raw); if (Number.isFinite(n)) setAxis(axis, n); }}
      />
      <div style={rowStyle}>
        {[-5, -1, 1, 5].map(d => (
          <button key={d} type="button" style={pillBtnStyle} onClick={() => setAxis(axis, value[axis] + d)} aria-label={`${axis.toUpperCase()} ${d > 0 ? '+' : ''}${d}%`}>
            {d > 0 ? `+${d}` : d}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div style={panelStyle} data-testid="tool-scale">
      {axisRow('x')}
      {axisRow('y')}
      <label style={checkRowStyle}>
        <input type="checkbox" checked={value.lock} style={checkboxStyle} data-testid="scale-lock"
          onChange={e => onChange(e.target.checked ? { ...value, lock: true, y: value.x } : { ...value, lock: false })} />
        Lock X and Y
      </label>
      <div style={rowStyle}>
        <button type="button" style={pillBtnStyle} onClick={() => onChange({ ...valueRef.current, x: 100, y: 100 })}>Reset</button>
        <button type="button" style={pillBtnStyle} onClick={() => { st().cancelToolPreview(); restart(); }}>Cancel</button>
        <button type="button" style={primaryPillStyle} onClick={apply}>Apply</button>
      </div>
      <div style={hintStyle}>
        Scales the notes, rails and walls in the selected area, or on the current beat, about the centre of
        the grid. Changes show as you make them; Apply keeps them. The Selection Options decide which kinds
        are included.
      </div>
    </div>
  );
}

// ── Rail Smoothing (RailSmoothingFunctions.cs) ──────────────────────────────

const DEFAULT_SMOOTHING: RailSmoothingParams = {
  algorithm: 'catmullRom', sliders: [5, 5, 5], anchor: true, trainingWheelsOff: false, scaleX: 0, scaleY: 0,
};

const fmtParam = (v: number, integer?: boolean) => (integer ? String(v) : String(Math.round(v * 1000) / 1000));

function RailSmoothingPanel({ onClose }: { onClose: () => void }) {
  const [opts, setOpts] = useState<RailSmoothingParams>(DEFAULT_SMOOTHING);
  const [noRail, setNoRail] = useState(false);
  const smoothing = useEditorStore(s => s.toolPreview === 'smooth');
  // The slider reads its handler once per drag, so the handlers read the latest options from here.
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const st = () => useEditorStore.getState();

  const preview = (next: RailSmoothingParams) => {
    setOpts(next);
    optsRef.current = next;
    setNoRail(st().previewRailSmoothing(next) === 'no-rail');
  };
  const ended = () => { const next = { ...optsRef.current, scaleX: 0, scaleY: 0 }; setOpts(next); optsRef.current = next; };

  const info = algorithmInfo(opts.algorithm);
  const scaling = (opts.scaleX ?? 0) >= 5 || (opts.scaleY ?? 0) >= 5;
  // As the desktop: once a slider is previewing, the +5 % buttons wait for Apply or Cancel.
  const scaleLocked = smoothing && !scaling;
  const bump = (x: boolean, y: boolean) => {
    if (scaleLocked) return;
    const o = optsRef.current;
    preview({ ...o, scaleX: x ? (o.scaleX ?? 0) + 5 : o.scaleX, scaleY: y ? (o.scaleY ?? 0) + 5 : o.scaleY });
  };

  return (
    <div style={panelStyle} data-testid="tool-smooth">
      <select
        value={opts.algorithm} style={selectStyle} aria-label="Smoothing algorithm"
        onChange={e => {
          // Switching algorithm drops an unapplied preview (the desktop cancels it too).
          st().cancelToolPreview();
          const next = { ...optsRef.current, algorithm: e.target.value as SmoothingAlgorithm, scaleX: 0, scaleY: 0 };
          setOpts(next); optsRef.current = next;
        }}
      >
        {SMOOTHING_ALGORITHMS.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
      </select>
      {info.params.map((p, i) => (
        <GradientSlider
          key={`${opts.algorithm}-${i}`}
          inline label={p.title} labelWidth={74}
          value={opts.sliders[i] / 10}
          valueText={fmtParam(paramValue(p, opts.sliders[i]), p.integer)}
          onChange={v => {
            const sliders = [...optsRef.current.sliders] as [number, number, number];
            sliders[i] = Math.round(v * 100) / 10;
            preview({ ...optsRef.current, sliders, scaleX: 0, scaleY: 0 });
          }}
        />
      ))}
      <label style={checkRowStyle}>
        <input type="checkbox" checked={opts.anchor} style={checkboxStyle} data-testid="smooth-anchor"
          onChange={e => { const next = { ...optsRef.current, anchor: e.target.checked }; if (smoothing) preview(next); else { setOpts(next); optsRef.current = next; } }} />
        Lock Endpoints
      </label>
      {opts.algorithm === 'catmullRom' && (
        <label style={checkRowStyle}>
          <input type="checkbox" checked={opts.trainingWheelsOff} style={checkboxStyle} data-testid="smooth-training-wheels"
            onChange={e => { const next = { ...optsRef.current, trainingWheelsOff: e.target.checked }; if (smoothing) preview(next); else { setOpts(next); optsRef.current = next; } }} />
          Training Wheels Off
        </label>
      )}
      <div style={subLabelStyle}>Scale up 5 %</div>
      <div style={rowStyle}>
        {([['X', true, false], ['Y', false, true], ['XY', true, true]] as const).map(([label, x, y]) => (
          <button key={label} type="button" disabled={scaleLocked} style={scaleLocked ? pillBtnDisabledStyle : pillBtnStyle} onClick={() => bump(x, y)}>
            +5% {label}
          </button>
        ))}
      </div>
      {scaling && <div style={hintStyle}>Scaled X {opts.scaleX ?? 0} %, Y {opts.scaleY ?? 0} %</div>}
      {smoothing && <div style={statusStyle}>Rail Smoothing active</div>}
      {noRail && <div style={statusStyle}>No rail starts on the current beat.</div>}
      <div style={rowStyle}>
        <button type="button" style={primaryPillStyle} onClick={() => { setNoRail(st().applyRailSmoothing(optsRef.current) === 'no-rail'); ended(); }}>Apply</button>
        <button type="button" style={pillBtnStyle} onClick={() => { st().cancelToolPreview(); ended(); }}>Cancel</button>
        <button type="button" style={primaryPillStyle} onClick={() => { if (st().toolPreview === 'smooth') st().commitToolPreview(); ended(); onClose(); }}>OK</button>
      </div>
      <div style={hintStyle}>
        Works on the rail that starts on the current beat. Moving the playhead cancels a preview that has not
        been applied.
      </div>
    </div>
  );
}
